import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import { alertService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout/Layout';

const EMPTY_RULE = {
  name: '',
  event_type: 'physical_missing_usage_term',
  condition_json: { days_ahead: 30 },
  recipients_json: { emails: [] },
  enabled: true,
  schedule: 'daily',
};

const SummaryCard = ({ title, value, subtitle }) => (
  <Paper sx={{ p: 2.2, height: '100%' }}>
    <Typography variant="h4" sx={{ lineHeight: 1.05 }}>
      {value}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.8, display: 'block' }}>
        {subtitle}
      </Typography>
    )}
  </Paper>
);

const Alerts = () => {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [eventTypes, setEventTypes] = useState({});
  const [draft, setDraft] = useState(EMPTY_RULE);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [runningCycle, setRunningCycle] = useState(false);
  const [processingRuleId, setProcessingRuleId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadData = async () => {
    try {
      setLoading(true);
      const [rulesResp, historyResp] = await Promise.all([
        alertService.getRules(),
        alertService.getHistory(),
      ]);

      setRules(rulesResp.rules || []);
      setEventTypes(rulesResp.event_types || {});
      setHistory(historyResp.notifications || []);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro ao carregar alertas', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openNewDialog = () => {
    setEditingRuleId(null);
    setDraft(EMPTY_RULE);
    setDialogOpen(true);
  };

  const openEditDialog = (rule) => {
    setEditingRuleId(rule.id);
    setDraft({
      ...rule,
      condition_json: rule.condition_json || { days_ahead: 30 },
      recipients_json: rule.recipients_json || { emails: [] },
    });
    setDialogOpen(true);
  };

  const saveRule = async () => {
    try {
      const payload = {
        ...draft,
        condition_json: {
          ...draft.condition_json,
          days_ahead: Number(draft.condition_json?.days_ahead || 30),
        },
        recipients_json: {
          emails: (draft.recipients_json?.emails || []).filter(Boolean),
        },
      };

      if (editingRuleId) {
        await alertService.updateRule(editingRuleId, payload);
      } else {
        await alertService.createRule(payload);
      }

      setDialogOpen(false);
      setDraft(EMPTY_RULE);
      await loadData();
      setSnackbar({ open: true, message: 'Regra salva com sucesso', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro ao salvar regra', severity: 'error' });
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      await alertService.deleteRule(ruleId);
      await loadData();
      setSnackbar({ open: true, message: 'Regra removida', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro ao remover regra', severity: 'error' });
    }
  };

  const testRule = async (ruleId) => {
    try {
      setProcessingRuleId(ruleId);
      const response = await alertService.testRule(ruleId);
      setSnackbar({
        open: true,
        message: `Teste concluído. Enviados: ${response.sent}, falhas: ${response.failed}`,
        severity: response.failed > 0 ? 'warning' : 'success',
      });
      await loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro ao testar regra', severity: 'error' });
    } finally {
      setProcessingRuleId(null);
    }
  };

  const toggleRule = async (rule) => {
    try {
      setProcessingRuleId(rule.id);
      await alertService.updateRule(rule.id, { enabled: !rule.enabled });
      await loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro ao atualizar status', severity: 'error' });
    } finally {
      setProcessingRuleId(null);
    }
  };

  const runNow = async () => {
    try {
      setRunningCycle(true);
      const response = await alertService.runNow();
      setSnackbar({
        open: true,
        message: `Ciclo executado. Regras: ${response.result.rules_processed}, eventos: ${response.result.events}, e-mails: ${response.result.emails}`,
        severity: 'success',
      });
      await loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro ao rodar ciclo', severity: 'error' });
    } finally {
      setRunningCycle(false);
    }
  };

  const activeRules = useMemo(() => rules.filter((rule) => rule.enabled).length, [rules]);
  const sentCount = useMemo(() => history.filter((item) => item.status === 'sent').length, [history]);
  const failedCount = useMemo(() => history.filter((item) => item.status === 'failed').length, [history]);

  if (user?.role !== 'admin' && user?.role !== 'manager') {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">Acesso restrito a administradores e gestores.</Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, maxWidth: 1280, mx: 'auto', width: '100%' }}>
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            mb: 2.5,
            border: 'none',
            color: 'common.white',
            background: 'linear-gradient(130deg, #1f5eff 0%, #00a387 100%)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h4" sx={{ mb: 0.6 }}>
                Alertas
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.95 }}>
                Configure regras de notificação e acompanhe os envios por canal.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={runNow}
                disabled={runningCycle}
                sx={{ borderColor: 'rgba(255,255,255,0.55)', color: 'common.white' }}
              >
                Rodar agora
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openNewDialog}
                sx={{ bgcolor: 'rgba(255,255,255,0.16)', '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' } }}
              >
                Nova regra
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={4}>
            <SummaryCard title="Regras cadastradas" value={rules.length} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard title="Regras ativas" value={activeRules} subtitle={`${rules.length - activeRules} desativadas`} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard title="Histórico (sent/failed)" value={`${sentCount}/${failedCount}`} subtitle={`${history.length} notificações no total`} />
          </Grid>
        </Grid>

        {loading && (
          <Paper sx={{ p: 2, mb: 2.2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Carregando regras e histórico...
            </Typography>
            <LinearProgress />
          </Paper>
        )}

        <Paper sx={{ p: 2.2, mb: 2.2 }}>
          <Typography variant="h6" sx={{ mb: 1.2 }}>
            Regras configuradas
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Evento</TableCell>
                  <TableCell>Destinatários</TableCell>
                  <TableCell>Ativa</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.id} hover>
                    <TableCell>
                      <Stack spacing={0.3}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {rule.name}
                        </Typography>
                        <Stack direction="row" spacing={0.6}>
                          <Chip size="small" label={rule.schedule || 'daily'} variant="outlined" />
                          <Chip size="small" label={rule.enabled ? 'ativa' : 'inativa'} color={rule.enabled ? 'success' : 'default'} />
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={eventTypes[rule.event_type] || rule.event_type} variant="outlined" />
                    </TableCell>
                    <TableCell>{(rule.recipients_json?.emails || []).join(', ') || '-'}</TableCell>
                    <TableCell>
                      <Switch
                        checked={!!rule.enabled}
                        onChange={() => toggleRule(rule)}
                        disabled={processingRuleId === rule.id}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Testar regra">
                        <span>
                          <IconButton size="small" onClick={() => testRule(rule.id)} disabled={processingRuleId === rule.id}>
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Editar regra">
                        <IconButton size="small" onClick={() => openEditDialog(rule)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir regra">
                        <IconButton size="small" onClick={() => deleteRule(rule.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && rules.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Nenhuma regra cadastrada.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper sx={{ p: 2.2 }}>
          <Typography variant="h6" sx={{ mb: 1.2 }}>
            Histórico de notificações
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Destinatário</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Canal</TableCell>
                  <TableCell>Enviado em</TableCell>
                  <TableCell>Erro</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>{item.recipient}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={item.status}
                        color={item.status === 'sent' ? 'success' : item.status === 'failed' ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{item.channel}</TableCell>
                    <TableCell>{item.sent_at ? new Date(item.sent_at).toLocaleString('pt-BR') : '-'}</TableCell>
                    <TableCell>{item.error_message || '-'}</TableCell>
                  </TableRow>
                ))}
                {!loading && history.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhum envio registrado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{editingRuleId ? 'Editar regra' : 'Nova regra'}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nome da regra"
                  value={draft.name}
                  onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Evento</InputLabel>
                  <Select
                    value={draft.event_type}
                    label="Evento"
                    onChange={(event) => setDraft((prev) => ({ ...prev, event_type: event.target.value }))}
                  >
                    {Object.entries(eventTypes).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  type="number"
                  fullWidth
                  label="Janela de dias (expiração)"
                  value={draft.condition_json?.days_ahead || 30}
                  onChange={(event) => {
                    const daysAhead = Number(event.target.value || 30);
                    setDraft((prev) => ({
                      ...prev,
                      condition_json: {
                        ...prev.condition_json,
                        days_ahead: daysAhead,
                      },
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="E-mails (separados por vírgula)"
                  value={(draft.recipients_json?.emails || []).join(', ')}
                  onChange={(event) => {
                    const emails = event.target.value
                      .split(',')
                      .map((item) => item.trim())
                      .filter(Boolean);
                    setDraft((prev) => ({
                      ...prev,
                      recipients_json: { emails },
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Agendamento</InputLabel>
                  <Select
                    value={draft.schedule || 'daily'}
                    label="Agendamento"
                    onChange={(event) => setDraft((prev) => ({ ...prev, schedule: event.target.value }))}
                  >
                    <MenuItem value="daily">Diário</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={saveRule}>
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default Alerts;
